import {Component, forwardRef, OnInit, ViewEncapsulation} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {GroupService} from '../../Services/groups.service';
import {PresetService} from '../../Services/preset.service';
import {PresetConfigurationModel} from '../../Models/preset-configuration-model';
import {CriminalService} from '../../Services/criminal.service';
import {CriminalModel, DecisionEnum, OverrideModel} from '../../Models/criminal-model';
import {ClientService} from '../../../clients-module/services/client.service';
import * as _ from 'lodash';
import {ObjectUpdatePayload} from '../../../models/screeningInvite';
import {AuthService} from '../../../services/auth.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.less'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SettingsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SettingsComponent),
      multi: true
    }
  ]
})
export class SettingsComponent implements ControlValueAccessor, OnInit {

  public form: UntypedFormGroup;
  public DecisionEnum = DecisionEnum;

  public clonedSetting: any;

  public constructor(
    private readonly criminalService: CriminalService,
    public readonly groupService: GroupService,
    public readonly presetService: PresetService,
    private readonly clientService: ClientService,
    private readonly auth: AuthService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly toast: ToastrService) {
  }

  public get f() {
    return this.form.controls;
  }

  // public onTouched: () => void = () => {}

  public registerOnChange(fn: (v: any) => void): void {
    this.form.valueChanges.subscribe(fn);
  }

  public setDisabledState(disabled: boolean): void {
    disabled ? this.form.disable() : this.form.enable();
  }

  public registerOnTouched(fn: () => void): void {
    // this.onTouched = fn;
  }

  public writeValue(value: CriminalModel | any): void {

    if (value) {

      this.form = this.formBuilder.group({
        crimeSubCategoryId: [value.crimeSubCategoryId, Validators.required],
        crimeSubCategoryFriendlyName: [value.crimeSubCategoryFriendlyName, Validators.required],
        settingId: [value.settingId],
        clientId: [this.clientService.currentClient.id, Validators.required],
        userId: [this.auth.loggedInUser.id, Validators.required],
        chargeType: [value.chargeType, Validators.required],
        howManyAllowed: [value.howManyAllowed, Validators.required],
        howLong: [value.howLong, Validators.required],
        decision: [value.decision, Validators.required],
        overrides: this.formBuilder.array([])
      });

      if (value.overrides) {
        value.overrides.forEach((e: OverrideModel) => {
          this.propertyOverride(e);
        });
      }

      this.createClonedSetting();

    }
  }

  public validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : {invalidForm: {valid: false, message: 'Form is invalid'}};
  }

  public ngOnInit(): void {

    this.presetService.presetSubject.subscribe((id: number) => {

      const selectedPreset = this.presetService.presets.find(e => e.id === id);

      if (selectedPreset) {

        selectedPreset.presetConfiguration.forEach((e: PresetConfigurationModel) => {

          if (this.form.controls.id.value === e.crimeId) {
            this.form.patchValue(e);
          }
        });
      }
    });
  }

  public trackByIndex(index, item) {
    if (!item) {
      return null;
    }
    return index;
  }

  public isFormDirty(): boolean {
    return this.form.controls.howManyAllowed.dirty ||
      this.form.controls.howLong.dirty ||
      this.form.controls.decision.dirty;
  }

  public isSubFormDirty(index: number) {
    const s = <UntypedFormArray>this.f.overrides;

    return s.controls[index].dirty;
  }

  public propertyOverride(e?: OverrideModel): void {
    const t = <UntypedFormArray>this.f.overrides;
    let item = new OverrideModel();

    if (e) {
      item = e;
    }

    const rowData = this.formBuilder.group({
      id: [item.id],
      crimeSubCategoryId: [this.form.controls.crimeSubCategoryId.value, Validators.required],
      clientId: [this.clientService.currentClient.id, Validators.required],
      userId: [this.auth.loggedInUser.id, Validators.required],
      groupId: [item.groupId, Validators.required],
      howManyAllowed: [item.howManyAllowed, Validators.required],
      howLong: [item.howLong, Validators.required],
      decision: [item.decision, Validators.required],
      chargeType: [this.form.controls.chargeType.value, Validators.required],
    });

    t.push(rowData);

    if (!e) {
      // we added a new one, opposed to rehydrating the form. mark this form as dirty to show effects
      t.controls[t.length - 1].markAsDirty();
      t.controls[t.length - 1].markAsTouched();
    }
  }

  public save(): void {

    const isValid = this.form.valid;
    const t = this.form.value;

    if (!isValid) {
      // ToDo display some message as why this form is invalid?
      return;
    }


    const clientId = this.form.controls.clientId.value;

    const updates = this.diff(this.clonedSetting, this.form.value, 'settingId');

    // set the client id and the user id
    _.forEach(updates, (a: ObjectUpdatePayload) => {
      a.clientId = this.form.controls.clientId.value;
      a.userId = this.form.controls.userId.value;
    });

    if (!this.form.controls.settingId.value) {
      // New setting.
      this.criminalService.addSetting(clientId, t).subscribe((e: any) => {

        this.form.markAsPristine();
        this.form.markAsUntouched();
        
        this.toast.success('Setting saved successfully', 'Save Successful');
        this.form.controls.settingId.setValue(e.id);

        this.createClonedSetting();

      });

    } else {

      const settingId = this.form.controls.settingId.value;

      this.criminalService.updateSetting(clientId, t).subscribe((e: any) => {
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.toast.success('Setting successfully modified', 'Save Successful');
        this.form.controls.settingId.setValue(e.id);

        this.createClonedSetting();

      });
    }
  }

  public saveOverride(index: number): void {

    const s = <UntypedFormArray>this.f.overrides;
    const isValid = s.controls[index].valid;
    const item = <OverrideModel>s.controls[index].value;

    if (!isValid) {
      // ToDo display some message as why this form is invalid?
      return;
    }

    this.criminalService.saveGroupOverride(item).subscribe((e: any) => {
      s.controls[index].markAsPristine();
      s.controls[index].markAsUntouched();
      s.controls[index].patchValue(e);
    });
  }

  public deleteOverride(index: number): void {
    const s = <UntypedFormArray>this.f.overrides;
    const item = <OverrideModel>s.controls[index].value;

    this.criminalService.removeGroupOverride(item.id, item.clientId).subscribe((e: any) => {
      s.removeAt(index);
    });
  }

  public resetSubFormDirty(index: number) {
    const s = <UntypedFormArray>this.f.overrides;
    s.removeAt(index);
  }

  public diff(obj1: any, obj2: any, idColumn: any): any | Array<ObjectUpdatePayload> | undefined {
    const result = {};

    if (Object.is(obj1, obj2)) {
      return undefined;
    }

    if (!obj2 || typeof obj2 !== 'object') {
      return obj2;
    }

    Object.keys(obj1 || {}).concat(Object.keys(obj2 || {})).forEach(key => {

      if ((obj2[key] !== obj1[key]) && !Object.is(obj1[key], obj2[key])) {

        let id = null;

        if (typeof (obj1[idColumn]) !== 'undefined') {
          id = obj1[idColumn];
        }

        if (id === null) {
          id = 0;
        }

        result[key] = {'propertyName': key, 'oldValue': obj1[key], 'newValue': obj2[key], 'objectId': id};
      }

      if (typeof obj2[key] === 'object' && typeof obj1[key] === 'object') {
        const value = this.diff(obj1[key], obj2[key], idColumn);

        if (value !== undefined) {
          result[key] = value;
          return;
        }
      }
    });

    return this.flatten(result);
  }

  public flatten(obj: any, prefix?: any, current?: any): Array<ObjectUpdatePayload> {
    prefix = prefix || [];
    current = current || Array<ObjectUpdatePayload>();

    if (typeof (obj) === 'object' && obj !== null) {

      if (typeof (obj.oldValue) !== 'undefined' || typeof (obj.newValue) !== 'undefined') {

        // Note, this only handles one deep.
        if (prefix.length === 2) {
          obj.objectName = prefix[0];
          current.push(obj);

        } else {
          obj.objectName = 'decisionGridSetting';
          current.push(obj);
        }

      } else {
        Object.keys(obj).forEach(key => {
          this.flatten(obj[key], prefix.concat(key), current);
        });
      }

    } else {
      current.push(obj);
    }

    return current;
  }

  private createClonedSetting(): void {
    this.clonedSetting = _.cloneDeep(this.form.value);
  }
}
